const duration = (seconds) => {
  const value = Math.max(0, Math.ceil(seconds));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
};
const numeric = (value) => ["number", "string"].includes(typeof value) &&
  String(value).trim() !== "" && Number.isFinite(Number(value)) ? Number(value) : null;
const stateOf = (step) => String(step.status || "PENDING").toUpperCase();
const ready = (step) => ["READY", "COMPLETED"].includes(stateOf(step));

/** Read-only reverse scheduling. No clock expiration is an acknowledgement or mutation. */
export function scheduleStationSteps(input, { dueAt, receivedAt, now = Date.now() }) {
  const fail = (error) => ({ steps: [], error });
  if (!Array.isArray(input) || !input.length) return fail("Station plan awaiting recipe timings.");
  if (![dueAt, receivedAt, now].every((value) => typeof value === "number" && Number.isFinite(value))) {
    return fail("Station plan needs a valid prep estimate and ordered time.");
  }
  const byId = new Map();
  for (const raw of input) {
    const seconds = numeric(raw?.durationSeconds);
    const handoff = raw?.handoffSeconds == null ? 0 : numeric(raw.handoffSeconds);
    if (!raw?.id || byId.has(String(raw.id)) || seconds == null || seconds <= 0 || handoff == null || handoff < 0 ||
      (raw.dependsOn != null && !Array.isArray(raw.dependsOn))) {
      return fail("Station recipe contains invalid durations, dependencies or step IDs.");
    }
    byId.set(String(raw.id), {
      ...raw, id: String(raw.id), name: raw.name || String(raw.id),
      stationName: raw.stationName || raw.stationId || "Station unspecified",
      durationSeconds: seconds, handoffSeconds: handoff,
      dependsOn: (raw.dependsOn || []).map(String), children: [],
    });
  }
  for (const step of byId.values()) {
    for (const id of step.dependsOn) {
      if (!byId.has(id)) return fail("Station recipe references an unavailable prerequisite.");
      byId.get(id).children.push(step.id);
    }
  }
  const visiting = new Set(), done = new Set();
  const plan = (step) => {
    if (visiting.has(step.id)) throw new Error("Station recipe contains a dependency cycle.");
    if (done.has(step.id)) return;
    visiting.add(step.id);
    step.children.forEach((id) => plan(byId.get(id)));
    const neededAt = step.children.length
      ? Math.min(...step.children.map((id) => byId.get(id).startAt)) : dueAt;
    step.finishAt = neededAt - step.handoffSeconds * 1000;
    step.startAt = step.finishAt - step.durationSeconds * 1000;
    step.infeasible = step.startAt < receivedAt;
    visiting.delete(step.id);
    done.add(step.id);
  };
  try { byId.forEach(plan); } catch (error) { return fail(error.message); }
  const steps = [...byId.values()].map((step) => {
    const blockedBy = step.dependsOn.filter((id) => !ready(byId.get(id))).map((id) => byId.get(id).name);
    const secondsUntilStart = (step.startAt - now) / 1000;
    let tone = "green", label = "Start in", display = duration(secondsUntilStart);
    if (ready(step)) { tone = "neutral"; label = "Ready"; display = "—"; }
    else if (["REMOVED", "REJECTED", "CANCELLED"].includes(stateOf(step))) {
      tone = "neutral"; label = "Cancelled"; display = "—";
    } else if (["IN_PROGRESS", "PROCESSING", "PREPARING", "STARTED"].includes(stateOf(step))) {
      tone = "neutral"; label = "Preparing"; display = "—";
    } else if (blockedBy.length) {
      tone = now > step.startAt ? "red" : "neutral";
      label = "Waiting for prerequisite"; display = secondsUntilStart > 0 ? duration(secondsUntilStart) : "—";
    } else if (secondsUntilStart <= 0 && secondsUntilStart > -1) {
      tone = "orange"; label = "Start now"; display = "00:00";
    } else if (secondsUntilStart < 0) {
      tone = "red"; label = "Start late by"; display = duration(-secondsUntilStart);
    } else if (secondsUntilStart <= 60) { tone = "orange"; }
    const { children, ...publicStep } = step;
    return { ...publicStep, blockedBy, tone, label, display };
  }).sort((a, b) => a.startAt - b.startAt);
  return { steps, error: "" };
}
